import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../../../config";
import "./UpdateAndSentPurposal.css";

const initialInsights = {
  score: 0,
  summary: "",
  risks: [],
  checklist: [],
  metrics: {},
};

function EditProposal() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    companyDescription: "",
    terms: "",
    status: "Pending",
    services: [],
    category: [],
    attachments: [],
    clientId: { leadName: "", emailId: "", phoneNo: "" },
  });

  const [newFiles, setNewFiles] = useState([]);
  const [insights, setInsights] = useState(initialInsights);
  const [analyzing, setAnalyzing] = useState(false);

  useEffect(() => {
    const fetchProposal = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/getProposalById/${id}`);
        const data = res.data;
        setFormData({
          ...data,
          companyDescription: data.companyDescription || "",
          clientId: {
            leadName: data.clientId?.leadName || "",
            emailId: data.clientId?.emailId || "",
            phoneNo: data.clientId?.phoneNo || "",
          },
        });
        if (data?.aiInsights) {
          setInsights(data.aiInsights);
        }
      } catch (err) {
        console.error("Error fetching proposal:", err);
      }
    };
    fetchProposal();
  }, [id]);

  const analyzeDraft = async (draft) => {
    try {
      setAnalyzing(true);
      const res = await axios.post(`${API_URL}/api/proposals/analyze`, {
        title: draft.title,
        description: draft.description,
        terms: draft.terms,
        services: draft.services,
        category: draft.category,
      });
      setInsights(res?.data?.insights || initialInsights);
      return res?.data?.insights;
    } catch (err) {
      console.error("Error analyzing proposal:", err?.response?.data || err);
      return null;
    } finally {
      setAnalyzing(false);
    }
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      analyzeDraft(formData);
    }, 350);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.title, formData.description, formData.terms, formData.services, formData.category]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleClientChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      clientId: {
        ...prev.clientId,
        [name]: value,
      },
    }));
  };

  const handleServiceChange = (index, field, value) => {
    const updatedServices = [...formData.services];
    updatedServices[index][field] = value;
    setFormData((prev) => ({ ...prev, services: updatedServices }));
  };

  const handleFileChange = (e) => {
    setNewFiles([...e.target.files]);
  };

  const totalPrice = formData.services?.reduce((acc, s) => acc + (s.price || 0), 0);

  const handleSave = async (sendEmail = true) => {
    try {
      const latestInsights = await analyzeDraft(formData);
      const data = new FormData();
      data.append("title", formData.title);
      data.append("description", formData.description);
      data.append("companyDescription", formData.companyDescription || "");
      data.append("terms", formData.terms);
      data.append("status", sendEmail ? "Sent" : (formData.status || "Draft"));
      data.append("sendEmail", sendEmail ? "true" : "false");
      data.append("services", JSON.stringify(formData.services));
      data.append("category", JSON.stringify(formData.category));

      formData.attachments.forEach((fileName) => {
        data.append("existingAttachments[]", fileName);
      });

      newFiles.forEach((file) => {
        data.append("attachments", file);
      });

      data.append("clientEmail", formData.clientId.emailId || "");
      data.append("clientName", formData.clientId.leadName || "");
      data.append("clientPhone", formData.clientId.phoneNo || "");

      const res = await axios.put(`${API_URL}/api/UpdateProposal/${id}`, data);
      const savedInsights = res?.data?.insights || latestInsights;
      if (savedInsights) setInsights(savedInsights);

      const message = res?.data?.warning || res?.data?.message || (sendEmail ? "Proposal updated & sent!" : "Proposal saved!");
      alert(message);
      navigate("/admin/PurposalList");
    } catch (err) {
      console.error("Error updating proposal:", err);
      const msg = err?.response?.data?.message || "Failed to update proposal. Check console.";
      if (String(msg).toLowerCase().includes("company email not configured")) {
        alert("Proposal updated successfully, but email was not sent (company email not configured).");
        navigate("/admin/PurposalList");
        return;
      }
      alert(msg);
    }
  };

  return (
    <div className="edit-proposal-page">
      <div className="edit-proposal-shell">
        <div className="edit-proposal-hero">
          <p className="edit-proposal-eyebrow">Sales CRM</p>
          <h2>Edit Proposal</h2>
          <span>Update proposal details, pricing, terms, and attachments before sending</span>
        </div>

        <div className="edit-proposal-card">
          <form>
            <div className="edit-proposal-grid edit-proposal-grid-3">
              <div className="edit-proposal-field">
                <label className="form-label">Client Name</label>
                <input
                  type="text"
                  name="leadName"
                  className="form-control edit-proposal-input"
                  value={formData.clientId?.leadName || ""}
                  onChange={handleClientChange}
                />
              </div>

              <div className="edit-proposal-field">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  name="emailId"
                  className="form-control edit-proposal-input"
                  value={formData.clientId?.emailId || ""}
                  onChange={handleClientChange}
                />
              </div>

              <div className="edit-proposal-field">
                <label className="form-label">Phone</label>
                <input
                  type="text"
                  name="phoneNo"
                  className="form-control edit-proposal-input"
                  value={formData.clientId?.phoneNo || ""}
                  onChange={handleClientChange}
                />
              </div>
            </div>

            <div className="edit-proposal-field">
              <label className="form-label">Title</label>
              <input
                type="text"
                name="title"
                className="form-control edit-proposal-input"
                value={formData.title || ""}
                onChange={handleChange}
              />
            </div>

             <div className="edit-proposal-field">
              <label className="form-label">Project Description</label>
              <textarea
                name="description"
                className="form-control edit-proposal-input edit-proposal-textarea"
                rows="3"
                value={formData.description || ""}
                onChange={handleChange}
              />
            </div>

            <div className="edit-proposal-field">
              <label className="form-label">Company Description</label>
              <textarea
                name="companyDescription"
                className="form-control edit-proposal-input edit-proposal-textarea"
                rows="3"
                value={formData.companyDescription || ""}
                onChange={handleChange}
              />
            </div>

            <div className="edit-proposal-field">
              <label className="form-label">Services</label>
              {formData.services?.map((s, index) => (
                <div key={index} className="edit-proposal-service-row">
                  <input
                    type="text"
                    value={s.name || ""}
                    onChange={(e) => handleServiceChange(index, "name", e.target.value)}
                    className="form-control edit-proposal-input"
                    placeholder="Service Name"
                  />
                  <input
                    type="number"
                    value={s.price || ""}
                    onChange={(e) => handleServiceChange(index, "price", Number(e.target.value))}
                    className="form-control edit-proposal-input"
                    placeholder="Price"
                  />
                </div>
              ))}
              <strong className="edit-proposal-total">Total Price: Rs {totalPrice}</strong>
            </div>

            <div className="edit-proposal-field">
              <label className="form-label">Terms</label>
              <input
                type="text"
                name="terms"
                className="form-control edit-proposal-input"
                value={formData.terms || ""}
                onChange={handleChange}
              />
            </div>

            <div className="mb-3 p-3 border rounded bg-light">
              <div className="d-flex justify-content-between align-items-center">
                <h6 className="mb-0">Proposal AI Insights</h6>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => analyzeDraft(formData)}
                  disabled={analyzing}
                >
                  {analyzing ? "Analyzing..." : "Recheck"}
                </button>
              </div>
              <div className="mt-2"><strong>Completeness Score:</strong> {insights.score || 0}/100</div>
              {insights.summary ? <div className="mt-1">{insights.summary}</div> : null}
              {insights.risks?.length > 0 ? (
                <ul className="mt-2 mb-0">
                  {insights.risks.map((risk) => (
                    <li key={risk.code}>
                      <strong>{risk.severity?.toUpperCase()}:</strong> {risk.message} {risk.suggestion}
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-success mt-2 mb-0">No major risks detected.</div>
              )}
            </div>

            <div className="edit-proposal-grid edit-proposal-grid-2">
              <div className="edit-proposal-field">
                <label className="form-label">Status</label>
                <select
                  name="status"
                  className="form-select edit-proposal-input"
                  value={formData.status || "Pending"}
                  onChange={handleChange}
                >
                  <option value="Sent">Send</option>
                  <option value="Draft">Draft</option>
                </select>
              </div>

              <div className="edit-proposal-field">
                <label className="form-label">Upload Files</label>
                <input type="file" multiple className="form-control edit-proposal-input" onChange={handleFileChange} />
                {formData.attachments?.length > 0 && (
                  <div className="edit-proposal-existing">
                    <strong>Existing files:</strong> {formData.attachments.join(", ")}
                  </div>
                )}
              </div>
            </div>

            <div className="edit-proposal-actions">
              <button type="button" className="btn edit-proposal-btn cancel" onClick={() => navigate("/admin/PurposalList")}>
                Cancel
              </button>
              <button type="button" className="btn edit-proposal-btn save-draft" style={{ backgroundColor: "#6c757d", color: "#fff" }} onClick={() => handleSave(false)}>
                Save
              </button>
              <button type="button" className="btn edit-proposal-btn save" onClick={() => handleSave(true)}>
                Save and Send
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default EditProposal;
